import { CoreClickData, CoreEvent, EventGeneric, SiteActivityComponent, TealiumEvent } from '@cms-max/cms-browser-api-client'
import { Activity } from '@cms-max/medicare-api-sdk'
import { FC, KeyboardEventHandler, MouseEventHandler, ReactNode, useRef } from 'react'
import { createEventWithDefaults } from 'utils/activity'

export interface MyLinkSiteActivityProps {
  clickData?: CoreClickData,
  component: SiteActivityComponent
}

export type MyLinkSiteActivityPropsV2 = EventGeneric<
  Omit<CoreEvent, 'activity' | 'property'>,
  TealiumEvent
>

export type MyLinkClickHandler = ({ linkUrl, linkText }: { linkUrl?: string, linkText?: string}) => void

export type MyLinkProps = Omit<JSX.IntrinsicElements['a'], 'onClick'> & {
  siteActivityProps?: MyLinkSiteActivityProps | MyLinkSiteActivityPropsV2,
  onClick?: MyLinkClickHandler
} & {
  children: ReactNode
}

export const sendLinkSiteActivity = (siteActivityProps?: MyLinkSiteActivityProps | MyLinkSiteActivityPropsV2) => {
  if (siteActivityProps) {
    const activityPropsv1 = siteActivityProps as MyLinkSiteActivityProps
    if (activityPropsv1.component) {
      window.globalHeaderEvents?.sendSiteActivityMetrics(
        Activity.LinkClick,
        'MAX',
        activityPropsv1.component,
        activityPropsv1.clickData
      )
    }

    const activityPropsv2 = siteActivityProps as MyLinkSiteActivityPropsV2
    if (activityPropsv2.core || activityPropsv2.tealium) {
      const defaults = {
        core: {
          property: 'MAX',
          activity: Activity.LinkClick
        }
      }
      const event = createEventWithDefaults(defaults, activityPropsv2)
      window.globalHeaderEvents?.handleEvent(event)
    }
  }
}

export const MyLink: FC<MyLinkProps> = ({ siteActivityProps, onClick, onKeyPress, children, ...props }) => {
  const ref = useRef<HTMLAnchorElement>(null)
  const handleClick: MouseEventHandler<HTMLAnchorElement> = (_event) => {
    sendLinkSiteActivity(siteActivityProps)
    onClick?.({
      linkUrl: props.href,
      linkText: ref.current?.innerText
    })
  }

  const handleKeyPress: KeyboardEventHandler<HTMLAnchorElement> = (event) => {
    if (event.nativeEvent.key === 'Enter') {
      onClick?.({
        linkUrl: props.href,
        linkText: ref.current?.innerText
      })
      sendLinkSiteActivity(siteActivityProps)
    }

    onKeyPress?.(event)
  }

  return (
    <a
      ref={ref}
      role='link'
      tabIndex={0}
      {...props}
      onKeyPress={handleKeyPress}
      onClick={handleClick}
    >
      {children}
    </a>
  )
}
